<script lang="ts">
  import type { Snippet } from 'svelte';
  import type { HTMLInputAttributes } from 'svelte/elements';

  // Form field: label + input + (error or hint). Wraps a single <input>
  // or <textarea> (controlled by `multiline`). Use `bind:value` from the
  // parent to read user input.
  //
  // The error/hint slot is intentionally a single line — for multi-line
  // help use a Banner above the form. Error wins over hint when both
  // are present so server validation always shows.

  interface Props {
    label: string;
    name: string;
    value?: string;
    type?: 'text' | 'email' | 'password' | 'url' | 'tel' | 'search' | 'number';
    placeholder?: string;
    autocomplete?: HTMLInputAttributes['autocomplete'];
    required?: boolean;
    disabled?: boolean;
    readonly?: boolean;
    /** Single-line error message (typically from ApiError.fieldError(name)). */
    error?: string | null;
    /** Single-line hint shown below the input when there's no error. */
    hint?: string;
    /** Render a textarea instead of an input. `rows` controls visible height. */
    multiline?: boolean;
    rows?: number;
    /** Optional adornment rendered to the right of the input (e.g. eye icon). */
    suffix?: Snippet;
    /** Optional input-level event handler — useful for live validation. */
    oninput?: (event: Event) => void;
    onblur?: (event: FocusEvent) => void;
  }

  let {
    label,
    name,
    value = $bindable(''),
    type = 'text',
    placeholder,
    autocomplete,
    required = false,
    disabled = false,
    readonly = false,
    error = null,
    hint,
    multiline = false,
    rows = 3,
    suffix,
    oninput,
    onblur,
  }: Props = $props();

  const id = $derived(`field-${name}`);
  const describedBy = $derived(error ? `${id}-error` : hint ? `${id}-hint` : undefined);
</script>

<div class="ui-field" class:ui-field--error={!!error} class:ui-field--disabled={disabled}>
  <label class="ui-field__label" for={id}>
    {label}
    {#if required}<span class="ui-field__required" aria-hidden="true">*</span>{/if}
  </label>

  <div class="ui-field__control">
    {#if multiline}
      <textarea
        class="ui-field__input"
        {id}
        {name}
        {placeholder}
        {required}
        {disabled}
        {readonly}
        {rows}
        aria-invalid={!!error}
        aria-describedby={describedBy}
        bind:value
        {oninput}
        {onblur}
      ></textarea>
    {:else}
      <input
        class="ui-field__input"
        {id}
        {name}
        {type}
        {placeholder}
        {autocomplete}
        {required}
        {disabled}
        {readonly}
        aria-invalid={!!error}
        aria-describedby={describedBy}
        bind:value
        {oninput}
        {onblur}
      />
    {/if}

    {#if suffix}
      <span class="ui-field__suffix">{@render suffix()}</span>
    {/if}
  </div>

  {#if error}
    <p class="ui-field__message ui-field__message--error" id="{id}-error">{error}</p>
  {:else if hint}
    <p class="ui-field__message ui-field__message--hint" id="{id}-hint">{hint}</p>
  {/if}
</div>
